import React, { memo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "@/hooks/useTranslation";
import { Save, X } from "lucide-react";

interface ExperienceFormData {
  company: string;
  position: string;
  location: string;
  employment_type: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  description: string;
}

interface ExperienceFormProps {
  formData: ExperienceFormData;
  onFormDataChange: (field: string, value: string | boolean) => void;
  onSave: (experienceId?: string) => void;
  onCancel: () => void;
  experienceId?: string;
}

const employmentTypes = [
  "Full-time", "Part-time", "Contract", "Internship", 
  "Freelance", "Volunteer", "Apprenticeship"
];

export const ExperienceForm = memo(({ 
  formData, 
  onFormDataChange, 
  onSave, 
  onCancel, 
  experienceId 
}: ExperienceFormProps) => {
  const { t } = useTranslation('ui');
  
  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          value={formData.position}
          onChange={(e) => onFormDataChange('position', e.target.value)}
          placeholder={t('experience_form.position_placeholder')}
          required
        />
        <Input
          value={formData.company}
          onChange={(e) => onFormDataChange('company', e.target.value)}
          placeholder={t('experience_form.company_placeholder')}
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Input
          value={formData.location}
          onChange={(e) => onFormDataChange('location', e.target.value)}
          placeholder={t('experience_form.location_placeholder')}
        />
        <Select 
          value={formData.employment_type} 
          onValueChange={(value) => onFormDataChange('employment_type', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('experience_form.employment_type_placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {employmentTypes.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="current"
            checked={formData.is_current}
            onCheckedChange={(checked) => {
              onFormDataChange('is_current', !!checked);
              if (checked) {
                onFormDataChange('end_date', '');
              }
            }}
          />
          <label htmlFor="current" className="text-sm">{t('experience_form.currently_working')}</label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-1 block">{t('experience_form.start_date_label')}</label>
          <Input
            type="date"
            value={formData.start_date}
            onChange={(e) => onFormDataChange('start_date', e.target.value)}
          />
        </div>
        {!formData.is_current && (
          <div>
            <label className="text-sm font-medium mb-1 block">{t('experience_form.end_date_label')}</label>
            <Input
              type="date"
              value={formData.end_date}
              onChange={(e) => onFormDataChange('end_date', e.target.value)}
            />
          </div>
        )}
      </div>

      <Textarea
        value={formData.description}
        onChange={(e) => onFormDataChange('description', e.target.value)}
        placeholder={t('experience_form.description_placeholder')}
        rows={3}
      />

      <div className="flex gap-2">
        <Button onClick={() => onSave(experienceId)} size="sm">
          <Save className="w-4 h-4 mr-2" />
          {t('experience_form.save')}
        </Button>
        <Button 
          onClick={onCancel} 
          variant="outline" 
          size="sm"
        >
          <X className="w-4 h-4 mr-2" />
          {t('experience_form.cancel')}
        </Button>
      </div>
    </div>
  );
});

ExperienceForm.displayName = "ExperienceForm";